import { Controller, useForm } from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { TextField } from "@mui/material";
import { MapLocationPicker_v2 } from "components/MapLocationPicker_v2";
import { useRegion_v2 } from "contexts/regions_v2";
import { ApartmentType, WaterMeterLocationType } from "types/dto";
import { Select } from "components/Select";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";

type TProps = {
  record?: any;
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (value: TAddFormTypes) => void;
  isLoading: boolean;
};

export type TAddFormTypes = {
  address: string;
  identifierId: string;
  numberOfFloor: number;
  // latitude: number;
  // longitude: number;
  location: { lat: number; lng: number } | null;
  jurisdictionId?: string;
  apartmentType: ApartmentType;
};

export function AddApartmentModal(props: TProps) {
  const { isOpen = false, onClose, onSubmit, isLoading } = props;

  const { selectedRegion } = useRegion_v2();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<TAddFormTypes>({
    defaultValues: {
      address: undefined,
      identifierId: undefined,
      // latitude: undefined,
      // longitude: undefined,
      location: null,
      numberOfFloor: undefined,
    },
  });

  return (
    <Dialog
      maxWidth={false}
      dialogTitle={t("addApartment")}
      open={isOpen}
      onClose={onClose}
      canClose={!isLoading}
    >
      <form className="p-4 w-[640px]" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col w-full gap-y-3">
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name={"identifierId"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("identifierId")}
                    placeholder={t("identifierId")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                  />
                );
              }}
            />
            <Controller
              name={"apartmentType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="flex-1"
                    label={t("apartmentType")}
                    options={SortObjectByPropName(
                      Enum2Array(ApartmentType),
                      "name"
                    )}
                    isError={!!error}
                  />
                );
              }}
            />
            <Controller
              name={"numberOfFloor"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("numberOfFloor")}
                    type={"number"}
                    placeholder={t("numberOfFloor")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"flex-1"}
                  />
                );
              }}
            />
          </div>
          <div className={"flex w-full gap-x-2"}>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <div className={"w-full h-[360px] flex-grow"}>
                  <MapLocationPicker_v2
                    className={"w-full h-full"}
                    onLocationPick={(coordinate) => {
                      field.onChange(coordinate);
                    }}
                    coordinateFields={"editable"}
                    showAddress
                    onAddressResolved={(addr) => {
                      // Fill only when the field is still empty; keep existing.
                      if (!getValues("address")) setValue("address", addr);
                    }}
                    onUseAddress={(addr) => setValue("address", addr)}
                  />
                </div>
              )}
            />
            {/*<Controller*/}
            {/*  name={"latitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("latitude")}*/}
            {/*        placeholder={t("latitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
            {/*<Controller*/}
            {/*  name={"longitude"}*/}
            {/*  control={control}*/}
            {/*  rules={{*/}
            {/*    required: true,*/}
            {/*  }}*/}
            {/*  render={({ field, fieldState: { error } }) => {*/}
            {/*    return (*/}
            {/*      <TextField*/}
            {/*        label={t("longitude")}*/}
            {/*        placeholder={t("longitude")}*/}
            {/*        type={"number"}*/}
            {/*        size="small"*/}
            {/*        {...field}*/}
            {/*        error={Boolean(error)}*/}
            {/*        className={"w-1/2"}*/}
            {/*      />*/}
            {/*    );*/}
            {/*  }}*/}
            {/*/>*/}
          </div>
          <RegionAutoComplete
            onChange={(value) => {
              setValue("jurisdictionId", value?.jurisdiction?.id);
            }}
            defaultValue={{
              provinceId: selectedRegion.province?.id,
              cityId: selectedRegion.city?.id,
              jurisdictionId: selectedRegion.jurisdiction?.id,
            }}
          />

          <Controller
            name={"address"}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("address")}
                  placeholder={t("address")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"w-full"}
                  multiline={true}
                  minRows={2}
                />
              );
            }}
          />
        </div>
        <div className="col-span-12 flex items-center justify-end mt-3">
          <Button
            className="w-20"
            loading={isLoading}
            type="submit"
            variant="contained"
          >
            {t("add")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
